import { memo, useMemo, useEffect, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import { MenuOrientation } from 'config';
import menuItems from 'menu-items';
import useConfig from 'hooks/useConfig';

// import { Menu } from 'menu-items/widget';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { useGetMenuMaster } from 'api/menu';
import useApi from '../../../hooks/useApi';
import PageMetaHiddenField from '@/commonComponent/PageMetaHiddenField';
import { AddDrawerProvider } from '../../../contexts/AddDrawerContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { fetchIcmEligibility } from '../../../hooks/useIcmEligibility';
// ==============================|| SIDEBAR MENU LIST ||============================== //

function MenuList() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { menuOrientation, showLoader, hideLoader } = useConfig();
  // const { menuLoading } = useGetMenu();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const [dataResponse, setDataResponse] = useState([]);
  const [selectedID, setSelectedID] = useState('');
  const [showLoaderValue, setShowLoaderValue] = useState(false);
  
  // Get hierarchy refresh trigger from context to re-fetch data when location settings are saved
  const { hierarchyRefreshTrigger, selectedType, currentNetwork } = useOrganization();

  // Start the ICM eligibility lookup as soon as a location or device group is selected, so the device-group
  // Instant WiFi entry (hidden until the answer is known) appears without waiting when it is allowed.
  // The answer is cached per network and the menu item reuses it.
  useEffect(() => {
    const scopeType = selectedType?.type;
    if (currentNetwork && (scopeType === 'Loc' || scopeType === 'CustomGroup')) {
      fetchIcmEligibility(currentNetwork);
    }
  }, [currentNetwork, selectedType?.type]);
  // const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  // let widgetMenu = Menu();

  // useLayoutEffect(() => {
  //   const isFound = menuItem.items.some((element) => {
  //     if (element.id === 'group-widget') {
  //       return true;
  //     }
  //     return false;
  //   });
  //   if (menuLoading) {
  //     menuItem.items.splice(1, 0, widgetMenu);
  //     setMenuItems({ items: [...menuItem.items] });
  //   } else if (!menuLoading && widgetMenu?.id !== undefined && !isFound) {
  //     menuItem.items.splice(1, 1, widgetMenu);
  //     setMenuItems({ items: [...menuItem.items] });
  //   } else {
  //     setMenuItems({ items: [...menuItem.items] });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [menuLoading]);

  // last menu-item to show in horizontal menu bar
  // below is the funciton to get the Org/Loc Tree View details from the API and set in the state
  const getOrgLocDetails = async () => {
    //   setDataResponse([
    //   {
    //     orgId: "org1",
    //     orgName: "TechWave Solutions",
    //     locCount: "2",
    //     locations: [
    //       {
    //         id: "net1",
    //         locationName: "Main Office Network",
    //         deviceGroupsCount: "3",
    //         customGroups: [
    //           { groupId: "dg1", groupName: "Printers" },
    //           { groupId: "dg2", groupName: "Workstations" },
    //           { groupId: "dg3", groupName: "IoT Devices" },
    //         ],
    //       },
    //       {
    //         id: "net2",
    //         locationName: "Remote Branch",
    //         deviceGroupsCount: "2",
    //         customGroups: [
    //           { groupId: "dg4", groupName: "Security Cameras" },
    //           { groupId: "dg5", groupName: "POS Systems" },
    //         ],
    //       },
    //     ],
    //   },
    //   {
    //     orgId: "org2",
    //     orgName: "CloudCore Inc",
    //     locCount: "1",
    //     locations: [
    //       {
    //         id: "net3",
    //         locationName: "HQ Network",
    //         deviceGroupsCount: "2",
    //         customGroups: [
    //           { groupId: "dg6", groupName: "Servers" },
    //           { groupId: "dg7", groupName: "Laptops" },
    //         ],
    //       },
    //     ],
    //   },
    // ])
    showLoader();
    try {
      const data = await useApi("GET_ORG_CHILD_DETAILS", "", "", ["_Id", "networkId"])
      if (data?.response?.status) {
        setDataResponse(data?.info);
      }
      hideLoader();
    } catch (error) {
      hideLoader();
      console.log("Error in Tree View :", error);
    }
  }
  useEffect(() => {
    // API call for the Org/Loc Tree View goes here:
    getOrgLocDetails();
  }, []);
  
  // Re-fetch hierarchy data when refreshHierarchyTree is called from other components (e.g., GeneralSettings save)
  useEffect(() => {
    if (hierarchyRefreshTrigger > 0) {
      getOrgLocDetailsTreeView();
    }
  }, [hierarchyRefreshTrigger])

  // --- SIDEBAR PageObject creation ---
  // Function to build a list preserving duplicate titles
const buildPageObjectsList = (nodes = []) => {
  return nodes.map((node) => {
    const title = node.title?.trim();
    const isGroup = Array.isArray(node.children) && node.children.length > 0;
    return {
      title, // keep visible title
      type: isGroup ? 'navGroup' : 'navItem',
      ...(isGroup && { children: buildPageObjectsList(node.children) })
    };
  });
};

// Optional legacy object form (not required if QA only uses list form)
const buildPageObjectsMap = (nodes = []) => {
  const result = {};
  nodes.forEach((node) => {
    const key = node.title?.trim();
    if (!key) return;
    if (Array.isArray(node.children) && node.children.length > 0) {
      result[key] = buildPageObjectsMap(node.children);
    } else {
      result[key] = 'navItem';
    }
  });
  return result;
};

// Memoized PageMeta (prevents rebuild on every render)
const PageMeta = useMemo(() => {
  const roots = Array.isArray(menuItems.items) ? menuItems.items : [menuItems];
  const topLevelChildren = roots.flatMap((r) => r.children || []);
  return {
    pageObjects: buildPageObjectsList(topLevelChildren)
  };
}, [menuItems]); // only recomputes if menuItems changes

  const getOrgLocDetailsTreeView = async () => {
    setShowLoaderValue(true);
    try {
      const data = await useApi("GET_ORG_CHILD_DETAILS", "", "", ["_Id", "networkId"])
      if (data?.response?.status) {
        setDataResponse(data?.info);
      }
      setShowLoaderValue(false);
    } catch (error) {
      setShowLoaderValue(false);
      console.log("Error in Tree View :", error);
    }
  }
  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;

  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navItems = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id}>
              <NavItem item={item} level={1} isParents setSelectedID={() => setSelectedID('')} />
              {!isHorizontal && index !== 0 && <Divider sx={{ py: 0.5 }} />}
            </List>
          );
        }

        return (
          <AddDrawerProvider key={item.id}>
            <NavGroup
              setSelectedID={setSelectedID}
              selectedID={selectedID}
              item={item}
              orgLocData={dataResponse}
              onOrgDropdownOpen={getOrgLocDetailsTreeView}
              showLoaderValue={showLoaderValue}
              lastItem={lastItem}
              remItems={remItems}
              lastItemId={lastItemId}
            />
          </AddDrawerProvider>
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const pageMetaField = (
    <PageMetaHiddenField
      meta={PageMeta}
      pageName="SidebarMenu"
      data-testid="page-meta-object"
      hidden
    />
  );

  return !isHorizontal ? <Box id="org-loc-menu-Id" {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}
    {pageMetaField}
  </Box> : <>{navItems}
    {pageMetaField}
  </>;
}

export default memo(MenuList);
